/* ============================================================================
  gsap-animations.js  (GENTLE / LESS INTERACTIVE)
  - Subtle scroll-triggered fade-in (older audience friendly)
  - Less movement, smaller stagger, no 3D flips
  - Uses GSAP + ScrollTrigger (consistent triggers, no “pop”)
  - Supports Shopify Theme Editor (section load/unload)
============================================================================ */

(function () {
  if (typeof window === "undefined") return;
  if (typeof window.gsap === "undefined") return;

  // Respect prefers-reduced-motion
  var reduceMotionQuery = window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

  if (reduceMotionQuery && reduceMotionQuery.matches) {
    // Make sure nothing stays hidden if reduced motion is on
    try {
      gsap.set(
        [
          ".js-gsap-heading",
          ".section-header",
          ".js-gsap-card",
          ".js-gsap-faq",
          ".js-gsap-rich-block",
          ".js-lotus-fade"
        ],
        { opacity: 1, x: 0, y: 0, rotateX: 0 }
      );
    } catch (e) {}
    return;
  }

  // ScrollTrigger is required for the smoother, less “interactive” behavior
  if (typeof window.ScrollTrigger === "undefined") return;

  gsap.registerPlugin(ScrollTrigger);

  /* -------------------------
    Global gentle settings
  ------------------------- */
  var GENTLE = {
    ease: "power2.out",
    duration: 0.95,
    y: 10,         // small vertical drift (was 22–30+)
    x: 14,         // small horizontal drift (was 32)
    stagger: 0.06, // smaller stagger (was 0.14–0.18)
    start: "top 88%"
  };

  // Store ScrollTriggers per root element so we can kill on unload
  var triggersByRoot = new WeakMap();

  function rememberTrigger(root, st) {
    if (!root || !st) return;
    var list = triggersByRoot.get(root);
    if (!list) list = [];
    list.push(st);
    triggersByRoot.set(root, list);
  }

  function killTriggersFor(root) {
    var list = triggersByRoot.get(root);
    if (list && list.length) {
      list.forEach(function (st) {
        try { st.kill(); } catch (e) {}
      });
    }
    triggersByRoot.delete(root);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel) || []);
  }

  function setHidden(root, selector, y, x) {
    var els = qsa(root, selector);
    if (!els.length) return;
    gsap.set(els, {
      opacity: 0,
      y: typeof y === "number" ? y : GENTLE.y,
      x: typeof x === "number" ? x : 0,
      willChange: "transform,opacity"
    });
  }

  function reveal(els, opts) {
    els = (els && els.length) ? els : [];
    if (!els.length) return;

    var o = opts || {};
    gsap.killTweensOf(els);

    gsap.to(els, {
      opacity: 1,
      x: 0,
      y: 0,
      duration: o.duration || GENTLE.duration,
      ease: o.ease || GENTLE.ease,
      stagger: o.stagger != null ? o.stagger : GENTLE.stagger,
      overwrite: "auto",
      onComplete: function () {
        // Remove will-change to avoid GPU “active” feeling
        try { gsap.set(els, { willChange: "auto" }); } catch (e) {}
      }
    });
  }

  function createOnceTrigger(root, triggerEl, start, onEnter) {
    if (!triggerEl) return null;

    var st = ScrollTrigger.create({
      trigger: triggerEl,
      start: start || GENTLE.start,
      once: true,
      onEnter: function () {
        try { onEnter && onEnter(); } catch (e) {}
      }
    });

    rememberTrigger(root, st);
    return st;
  }

  /* -------------------------
    Preset initial hidden states
    (prevents “blink” before animation)
  ------------------------- */
  function presetInitialStates(root) {
    root = root || document;

    // HERO MEDICATED
    setHidden(
      root,
      ".hero-medicated .js-gsap-heading," +
        ".hero-medicated .js-gsap-hero-description," +
        ".hero-medicated .js-gsap-hero-button," +
        ".hero-medicated .js-gsap-hero-badge",
      GENTLE.y
    );

    // FAQ (no rotateX anymore)
    setHidden(
      root,
      ".faq-section .faq__heading," +
        ".faq-section .js-gsap-faq," +
        ".faq-section .accordion_footer h3," +
        ".faq-section .accordion_footer .button-tertiary",
      GENTLE.y
    );

    // SIMPLE STEPS header + nav
    setHidden(
      root,
      ".simple-steps__heading," +
        ".simple-steps__subheading," +
        ".simple-steps__cta--desktop," +
        ".simple-steps__cta--mobile," +
        ".simple-steps__nav",
      GENTLE.y
    );

    // GRID CARDS (universal)
    setHidden(root, ".gsap-section-grid-animation .js-gsap-card", GENTLE.y);

    // RICH TEXT
    setHidden(
      root,
      ".rich-text-section .rich-text__section-heading," +
        ".rich-text-section .rich-text__section-subheading," +
        ".rich-text-section .js-gsap-rich-block",
      GENTLE.y
    );

    // COMPARE PANELS
    setHidden(
      root,
      ".compare-panels-section .compare-panels__heading," +
        ".compare-panels-section .compare-panels__subheading," +
        ".compare-panels-section .compare-panels__table tbody tr",
      GENTLE.y
    );

    // SLIMMR PARTNER BENEFITS
    setHidden(root, ".slimmr-partner-benefits .slimmr-partner-benefits__heading", GENTLE.y);

    // IMAGE-TEXT
    setHidden(
      root,
      ".image-text-section .js-gsap-image-text-media," +
        ".image-text-section .image-text__heading," +
        ".image-text-section .js-gsap-image-text-body," +
        ".image-text-section .js-gsap-image-text-source," +
        ".image-text-section .js-gsap-image-text-button",
      GENTLE.y
    );

    // PRODUCT INFO
    setHidden(
      root,
      ".product-info-section .js-gsap-product-info-left," +
        ".product-info-section .js-gsap-product-info-review," +
        ".product-info-section .js-gsap-product-info-header," +
        ".product-info-section .js-gsap-product-info-icons," +
        ".product-info-section .js-gsap-product-info-price," +
        ".product-info-section .js-gsap-product-info-buttons," +
        ".product-info-section .js-gsap-product-info-note",
      GENTLE.y
    );

    // PRICING TABLE
    setHidden(
      root,
      ".pricing-table-section .pricing-table__heading," +
        ".pricing-table-section .pricing-table__row," +
        ".pricing-table-section .pricing-table__button," +
        ".pricing-table-section .pricing-table__note",
      GENTLE.y
    );

    // FEATURE BANNER
    setHidden(
      root,
      ".feature-banner .js-gsap-feature-media," +
        ".feature-banner .js-gsap-feature-logo," +
        ".feature-banner .js-gsap-feature-heading," +
        ".feature-banner .js-gsap-feature-text," +
        ".feature-banner .js-gsap-feature-button",
      GENTLE.y
    );

    // HOW TABS
    setHidden(
      root,
      ".how-tabs-section .how-tabs-section__heading," +
        ".how-tabs-section .how-tabs-section__subheading," +
        ".how-tabs-section .how-tabs-section__tab," +
        ".how-tabs-section .js-gsap-how-card",
      GENTLE.y
    );

    // INTRO ICONS TOP
    setHidden(root, ".intro-icons-section .intro-icons__top", GENTLE.y);

    // BANNER SECTION
    setHidden(
      root,
      ".banner-section-section .bannner_section_main_heading," +
        ".banner-section-section .banner_section_des," +
        ".banner-section-section .banner-section__heading," +
        ".banner-section-section .banner-section__text," +
        ".banner-section-section .banner-section__button",
      GENTLE.y
    );

    // COLLECTION INTRO
    setHidden(
      root,
      ".collection-intro-section .js-gsap-collection-review," +
        ".collection-intro-section .collection-intro__heading," +
        ".collection-intro-section .collection-intro__text," +
        ".collection-intro-section .js-gsap-collection-media",
      GENTLE.y
    );

    // COLLECTION BANNER
    setHidden(
      root,
      ".collection-banner-section .js-gsap-collection-banner-media," +
        ".collection-banner-section .js-gsap-collection-banner-heading," +
        ".collection-banner-section .js-gsap-collection-banner-text," +
        ".collection-banner-section .js-gsap-collection-banner-button," +
        ".collection-banner-section .js-gsap-collection-banner-tagline",
      GENTLE.y
    );

    // COLLECTION CARDS (heading/subheading)
    setHidden(
      root,
      ".collection-cards-section .collection-cards__heading," +
        ".collection-cards-section .collection-cards__subheading",
      GENTLE.y
    );

    // ICON TEXT (heading/nav)
    setHidden(
      root,
      ".icon-text-section .icon-text__section-heading," +
        ".icon-text-section .simple-steps__nav",
      GENTLE.y
    );

    // LOTUS TIMELINE (your new requirement)
    setHidden(root, ".lotus-timeline-section .js-lotus-fade", 0); // fade-only for containers
  }

  /* -------------------------
    Section Animations (gentle)
  ------------------------- */

  // 1) HERO MEDICATED
  function initHeroMedicatedAnimations(root) {
    qsa(root, ".hero-medicated").forEach(function (section) {
      if (section.dataset.gsapHeroAnimated === "true") return;
      section.dataset.gsapHeroAnimated = "true";

      var els = [
        section.querySelector(".js-gsap-hero-badge"),
        section.querySelector(".js-gsap-heading"),
        section.querySelector(".js-gsap-hero-description"),
        section.querySelector(".js-gsap-hero-button")
      ].filter(Boolean);

      createOnceTrigger(section, section, "top 80%", function () {
        reveal(els, { stagger: 0.08, duration: 0.95 });
      });
    });
  }

  // 2) UNIVERSAL GRID CARDS (staggered but subtle)
  function initGsapCardGrids(root) {
    qsa(root, ".gsap-section-grid-animation").forEach(function (grid) {
      if (grid.dataset.gsapGridAnimated === "true") return;
      grid.dataset.gsapGridAnimated = "true";

      var cards = qsa(grid, ".js-gsap-card");
      if (!cards.length) return;

      // Batch makes multiple cards that enter together feel calmer
      var st = ScrollTrigger.batch(cards, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          reveal(batch, { stagger: 0.07, duration: 0.9 });
        }
      });

      // ScrollTrigger.batch returns an array of triggers in some versions
      // Normalize storing:
      if (Array.isArray(st)) {
        st.forEach(function (t) { rememberTrigger(grid, t); });
      } else {
        rememberTrigger(grid, st);
      }
    });
  }

  // 3) SIMPLE STEPS header/nav
  function initSimpleStepsAnimations(root) {
    qsa(root, ".simple-steps").forEach(function (section) {
      if (section.dataset.gsapSimpleStepsAnimated === "true") return;
      section.dataset.gsapSimpleStepsAnimated = "true";

      var els = [
        section.querySelector(".simple-steps__heading"),
        section.querySelector(".simple-steps__subheading"),
        section.querySelector(".simple-steps__cta--desktop"),
        section.querySelector(".simple-steps__nav"),
        section.querySelector(".simple-steps__cta--mobile")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(els, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 4) IMAGE TEXT (keep tiny side motion for media only)
  function initImageTextAnimations(root) {
    qsa(root, ".image-text-section").forEach(function (section) {
      if (section.dataset.gsapImageTextAnimated === "true") return;
      section.dataset.gsapImageTextAnimated = "true";

      var card = section.querySelector(".image-text__card");
      var media = section.querySelector(".js-gsap-image-text-media");

      var heading = section.querySelector(".image-text__heading");
      var body = section.querySelector(".js-gsap-image-text-body");
      var source = section.querySelector(".js-gsap-image-text-source");
      var button = section.querySelector(".js-gsap-image-text-button");
      var contentEls = [heading, body, source, button].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        // media: slight x + tiny y
        if (media) {
          var fromX = 0;
          var isImageRight = card && card.classList.contains("image-text-left");
          var isImageLeft = card && card.classList.contains("image-text-right");
          if (isImageLeft) fromX = -GENTLE.x;
          if (isImageRight) fromX = GENTLE.x;

          gsap.killTweensOf(media);
          gsap.to(media, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 1.0,
            ease: GENTLE.ease,
            overwrite: "auto",
            onStart: function () {
              // ensure initial state is gentle
              gsap.set(media, { opacity: 0, x: fromX, y: 4, willChange: "transform,opacity" });
            },
            onComplete: function () {
              gsap.set(media, { willChange: "auto" });
            }
          });
        }

        // text: mostly fade-up
        reveal(contentEls, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 5) FEATURE BANNER (gentle)
  function initFeatureBannerAnimations(root) {
    qsa(root, ".feature-banner").forEach(function (section) {
      if (section.dataset.gsapFeatureBannerAnimated === "true") return;
      section.dataset.gsapFeatureBannerAnimated = "true";

      var media = section.querySelector(".js-gsap-feature-media");
      var contentEls = [
        section.querySelector(".js-gsap-feature-logo"),
        section.querySelector(".js-gsap-feature-heading"),
        section.querySelector(".js-gsap-feature-text"),
        section.querySelector(".js-gsap-feature-button")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        if (media) {
          gsap.killTweensOf(media);
          gsap.set(media, { opacity: 0, x: GENTLE.x, y: 4, willChange: "transform,opacity" });
          gsap.to(media, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 1.0,
            ease: GENTLE.ease,
            overwrite: "auto",
            onComplete: function () { gsap.set(media, { willChange: "auto" }); }
          });
        }
        reveal(contentEls, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 6) BANNER SECTION
  function initBannerSectionAnimations(root) {
    qsa(root, ".banner-section-section").forEach(function (section) {
      if (section.dataset.gsapBannerSectionAnimated === "true") return;
      section.dataset.gsapBannerSectionAnimated = "true";

      var els = [
        section.querySelector(".bannner_section_main_heading"),
        section.querySelector(".banner_section_des"),
        section.querySelector(".banner-section__heading"),
        section.querySelector(".banner-section__text"),
        section.querySelector(".banner-section__button.button-tertiary.mobile-hidden"),
        section.querySelector(".banner-section__button.button-secondary.desktop-hidden"),
        section.querySelector(".banner-section__button")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(els, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 7) ICON TEXT header/nav (cards handled by grid init)
  function initIconTextAnimations(root) {
    qsa(root, ".icon-text-section").forEach(function (section) {
      if (section.dataset.gsapIconTextAnimated === "true") return;
      section.dataset.gsapIconTextAnimated = "true";

      var els = [
        section.querySelector(".icon-text__section-heading"),
        section.querySelector(".simple-steps__nav")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(els, { stagger: 0.0, duration: 0.95 }); // no stagger needed here
      });
    });
  }

  // 8) RICH TEXT
  function initRichTextAnimations(root) {
    qsa(root, ".rich-text-section").forEach(function (section) {
      if (section.dataset.gsapRichTextAnimated === "true") return;
      section.dataset.gsapRichTextAnimated = "true";

      var headerEls = [
        section.querySelector(".rich-text__section-heading"),
        section.querySelector(".rich-text__section-subheading")
      ].filter(Boolean);

      var blocks = qsa(section, ".js-gsap-rich-block");

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(headerEls, { stagger: 0.05, duration: 0.95 });
        reveal(blocks, { stagger: 0.05, duration: 0.95 });
      });
    });
  }

  // 9) FAQ (no rotateX)
  function initFaqAnimations(root) {
    qsa(root, ".faq-section").forEach(function (section) {
      if (section.dataset.gsapFaqAnimated === "true") return;
      section.dataset.gsapFaqAnimated = "true";

      var heading = section.querySelector(".faq__heading");
      var items = qsa(section, ".js-gsap-faq");
      var footerEls = [
        section.querySelector(".accordion_footer h3"),
        section.querySelector(".accordion_footer .button-tertiary")
      ].filter(Boolean);

      createOnceTrigger(section, section, "top 88%", function () {
        reveal([heading].filter(Boolean), { stagger: 0.0, duration: 0.95 });
        reveal(items, { stagger: 0.05, duration: 0.9 });
        reveal(footerEls, { stagger: 0.05, duration: 0.9 });
      });
    });
  }

  // 10) PRICING TABLE
  function initPricingTableAnimations(root) {
    qsa(root, ".pricing-table-section").forEach(function (section) {
      if (section.dataset.gsapPricingTableAnimated === "true") return;
      section.dataset.gsapPricingTableAnimated = "true";

      var heading = section.querySelector(".pricing-table__heading");
      var rows = qsa(section, ".pricing-table__row");
      var footerEls = [
        section.querySelector(".pricing-table__button"),
        section.querySelector(".pricing-table__note")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal([heading].filter(Boolean), { stagger: 0.0, duration: 0.95 });
        reveal(rows, { stagger: 0.04, duration: 0.9 });
        reveal(footerEls, { stagger: 0.05, duration: 0.9 });
      });
    });
  }

  // 11) COMPARE PANELS
  function initComparePanelsAnimations(root) {
    qsa(root, ".compare-panels-section").forEach(function (section) {
      if (section.dataset.gsapComparePanelsAnimated === "true") return;
      section.dataset.gsapComparePanelsAnimated = "true";

      var headerEls = [
        section.querySelector(".compare-panels__heading"),
        section.querySelector(".compare-panels__subheading")
      ].filter(Boolean);

      var rows =
        qsa(section, ".js-gsap-compare-row").length
          ? qsa(section, ".js-gsap-compare-row")
          : qsa(section, ".compare-panels__table tbody tr");

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(headerEls, { stagger: 0.05, duration: 0.95 });
        reveal(rows, { stagger: 0.04, duration: 0.9 });
      });
    });
  }

  // 12) INTRO ICONS (top only; cards already via grid)
  function initIntroIconsAnimations(root) {
    qsa(root, ".intro-icons-section").forEach(function (section) {
      if (section.dataset.gsapIntroIconsAnimated === "true") return;
      section.dataset.gsapIntroIconsAnimated = "true";

      var top = section.querySelector(".intro-icons__top");
      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal([top].filter(Boolean), { stagger: 0.0, duration: 0.95 });
      });
    });
  }

  // 13) COLLECTION INTRO
  function initCollectionIntroAnimations(root) {
    qsa(root, ".collection-intro-section").forEach(function (section) {
      if (section.dataset.gsapCollectionIntroAnimated === "true") return;
      section.dataset.gsapCollectionIntroAnimated = "true";

      var media = section.querySelector(".js-gsap-collection-media");
      var contentEls = [
        section.querySelector(".js-gsap-collection-review"),
        section.querySelector(".collection-intro__heading"),
        section.querySelector(".collection-intro__text")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        if (media) {
          gsap.killTweensOf(media);
          gsap.set(media, { opacity: 0, x: GENTLE.x, y: 4, willChange: "transform,opacity" });
          gsap.to(media, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 1.0,
            ease: GENTLE.ease,
            overwrite: "auto",
            onComplete: function () { gsap.set(media, { willChange: "auto" }); }
          });
        }
        reveal(contentEls, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 14) COLLECTION BANNER
  function initCollectionBannerAnimations(root) {
    qsa(root, ".collection-banner-section").forEach(function (section) {
      if (section.dataset.gsapCollectionBannerAnimated === "true") return;
      section.dataset.gsapCollectionBannerAnimated = "true";

      var media = section.querySelector(".js-gsap-collection-banner-media");
      var contentEls = [
        section.querySelector(".js-gsap-collection-banner-heading"),
        section.querySelector(".js-gsap-collection-banner-text"),
        section.querySelector(".js-gsap-collection-banner-button"),
        section.querySelector(".js-gsap-collection-banner-tagline")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        if (media) {
          gsap.killTweensOf(media);
          gsap.set(media, { opacity: 0, x: GENTLE.x, y: 4, willChange: "transform,opacity" });
          gsap.to(media, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 1.0,
            ease: GENTLE.ease,
            overwrite: "auto",
            onComplete: function () { gsap.set(media, { willChange: "auto" }); }
          });
        }
        reveal(contentEls, { stagger: 0.06, duration: 0.95 });
      });
    });
  }

  // 15) PRODUCT INFO
  function initProductInfoAnimations(root) {
    qsa(root, ".product-info-section").forEach(function (section) {
      if (section.dataset.gsapProductInfoAnimated === "true") return;
      section.dataset.gsapProductInfoAnimated = "true";

      var left = section.querySelector(".js-gsap-product-info-left");
      var contentEls = [
        section.querySelector(".js-gsap-product-info-review"),
        section.querySelector(".js-gsap-product-info-header"),
        section.querySelector(".js-gsap-product-info-icons"),
        section.querySelector(".js-gsap-product-info-price"),
        section.querySelector(".js-gsap-product-info-buttons"),
        section.querySelector(".js-gsap-product-info-note")
      ].filter(Boolean);

      createOnceTrigger(section, section, "top 85%", function () {
        if (left) {
          gsap.killTweensOf(left);
          gsap.set(left, { opacity: 0, x: -GENTLE.x, y: 4, willChange: "transform,opacity" });
          gsap.to(left, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 1.0,
            ease: GENTLE.ease,
            overwrite: "auto",
            onComplete: function () { gsap.set(left, { willChange: "auto" }); }
          });
        }
        reveal(contentEls, { stagger: 0.05, duration: 0.95 });
      });
    });
  }

  // 16) HOW TABS
  function initHowTabsAnimations(root) {
    qsa(root, ".how-tabs-section").forEach(function (section) {
      if (section.dataset.gsapHowTabsAnimated === "true") return;
      section.dataset.gsapHowTabsAnimated = "true";

      var headerEls = [
        section.querySelector(".how-tabs-section__heading"),
        section.querySelector(".how-tabs-section__subheading")
      ].filter(Boolean);

      var tabs = qsa(section, ".how-tabs-section__tab");
      var cards = qsa(section, ".js-gsap-how-card");

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(headerEls, { stagger: 0.05, duration: 0.95 });
        reveal(tabs, { stagger: 0.04, duration: 0.85 });
        reveal(cards, { stagger: 0.05, duration: 0.9 });
      });
    });
  }

  // 17) COLLECTION CARDS (heading/subheading only; cards animated by grid if you used it)
  function initCollectionCardsAnimations(root) {
    qsa(root, ".collection-cards-section").forEach(function (section) {
      if (section.dataset.gsapCollectionCardsAnimated === "true") return;
      section.dataset.gsapCollectionCardsAnimated = "true";

      var els = [
        section.querySelector(".collection-cards__heading"),
        section.querySelector(".collection-cards__subheading")
      ].filter(Boolean);

      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal(els, { stagger: 0.05, duration: 0.95 });
      });
    });
  }

  // 18) SLIMMR PARTNER BENEFITS heading
  function initSlimmrPartnerBenefitsAnimations(root) {
    qsa(root, ".slimmr-partner-benefits").forEach(function (section) {
      if (section.dataset.gsapSlimmrPartnerBenefitsAnimated === "true") return;
      section.dataset.gsapSlimmrPartnerBenefitsAnimated = "true";

      var heading = section.querySelector(".slimmr-partner-benefits__heading");
      createOnceTrigger(section, section, GENTLE.start, function () {
        reveal([heading].filter(Boolean), { stagger: 0.0, duration: 0.95 });
      });
    });
  }

  // 19) LOTUS TIMELINE containers: fade-only + gentle stagger if multiple
  //   Add .js-lotus-fade to:
  //     - .lotus-timeline-section__intro-inner
  //     - each article.lotus-timeline-section__item
  function initLotusTimelineAnimations(root) {
    qsa(root, ".lotus-timeline-section").forEach(function (section) {
      if (section.dataset.gsapLotusAnimated === "true") return;
      section.dataset.gsapLotusAnimated = "true";

      var targets = qsa(section, ".js-lotus-fade");
      if (!targets.length) return;

      // Fade-only: no movement (client asked for opacity only)
      gsap.set(targets, { opacity: 0, y: 0, x: 0, willChange: "opacity" });

      // Batch for gentle stagger
      var st = ScrollTrigger.batch(targets, {
        start: "top 90%",
        once: true,
        onEnter: function (batch) {
          gsap.killTweensOf(batch);
          gsap.to(batch, {
            opacity: 1,
            duration: 1.05,
            ease: "power2.out",
            stagger: 0.08,
            overwrite: "auto",
            onComplete: function () {
              try { gsap.set(batch, { willChange: "auto" }); } catch (e) {}
            }
          });
        }
      });

      if (Array.isArray(st)) st.forEach(function (t) { rememberTrigger(section, t); });
      else rememberTrigger(section, st);
    });
  }

  /* -------------------------
    INIT ALL
  ------------------------- */
  function initAll(root) {
    root = root || document;

    presetInitialStates(root);

    initHeroMedicatedAnimations(root);
    initGsapCardGrids(root);
    initSimpleStepsAnimations(root);
    initImageTextAnimations(root);
    initFeatureBannerAnimations(root);
    initBannerSectionAnimations(root);
    initIconTextAnimations(root);
    initCollectionIntroAnimations(root);
    initCollectionCardsAnimations(root);
    initCollectionBannerAnimations(root);
    initFaqAnimations(root);
    initRichTextAnimations(root);
    initIntroIconsAnimations(root);
    initProductInfoAnimations(root);
    initPricingTableAnimations(root);
    initComparePanelsAnimations(root);
    initSlimmrPartnerBenefitsAnimations(root);
    initHowTabsAnimations(root);
    initLotusTimelineAnimations(root);

    // Helps when Shopify loads sections dynamically
    try { ScrollTrigger.refresh(); } catch (e) {}
  }

  // Normal load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify Theme Editor support
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    // Kill triggers inside the unloaded section to prevent “interactive” leftovers
    var root = event.target;
    killTriggersFor(root);

    // Also kill triggers created for nested grids/sections inside this root
    // (best-effort cleanup)
    qsa(root, ".gsap-section-grid-animation").forEach(killTriggersFor);
    qsa(root, ".lotus-timeline-section").forEach(killTriggersFor);
  });
})();
